import React from 'react';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import { useContent } from '../context/ContentContext';
import { Sparkles, Users, Grid3x3, ChefHat, ArrowRight, CheckCircle } from 'lucide-react';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { seoData, translations } from '../data/translations';
import { useLanguage } from '../context/LanguageContext';

const iconMap = { sparkles: Sparkles, users: Users, grid3x3: Grid3x3, chefHat: ChefHat };

// Golden Glow Line Component
const GoldenGlowLine = () => (
  <div className="relative h-[2px] bg-black overflow-hidden">
    <div 
      className="absolute top-0 h-full w-[300px]"
      style={{
        background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
        animation: 'glowLine 4s ease-in-out infinite',
        boxShadow: '0 0 20px #D4AF37, 0 0 40px #D4AF37',
      }}
    />
  </div>
);

// Animated Full Frame Glow Component (animation in index.css)
const AnimatedFullFrame = ({ children, className = "" }) => (
  <div className={`group relative ${className}`}>
    {/* Top line */}
    <div className="absolute top-0 left-0 right-0 h-[2px] bg-black z-10 overflow-hidden">
      <div 
        className="absolute top-0 h-full w-[100px]"
        style={{
          background: 'linear-gradient(90deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
          animation: 'frameGlowTop 3s ease-in-out infinite',
          boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
        }}
      />
    </div>
    
    {/* Right line */}
    <div className="absolute top-0 right-0 bottom-0 w-[2px] bg-black z-10 overflow-hidden">
      <div 
        className="absolute right-0 w-full h-[100px]"
        style={{
          background: 'linear-gradient(180deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
          animation: 'frameGlowRight 3s ease-in-out infinite',
          animationDelay: '0.75s',
          boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
        }}
      />
    </div>
    
    {/* Bottom line */}
    <div className="absolute bottom-0 left-0 right-0 h-[2px] bg-black z-10 overflow-hidden">
      <div 
        className="absolute bottom-0 h-full w-[100px]"
        style={{
          background: 'linear-gradient(270deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
          animation: 'frameGlowBottom 3s ease-in-out infinite',
          animationDelay: '1.5s',
          boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
        }}
      />
    </div>
    
    {/* Left line */}
    <div className="absolute top-0 left-0 bottom-0 w-[2px] bg-black z-10 overflow-hidden">
      <div 
        className="absolute left-0 w-full h-[100px]"
        style={{
          background: 'linear-gradient(0deg, transparent, #D4AF37, #FFD700, #D4AF37, transparent)',
          animation: 'frameGlowLeft 3s ease-in-out infinite',
          animationDelay: '2.25s',
          boxShadow: '0 0 15px #D4AF37, 0 0 30px #D4AF37',
        }}
      />
    </div>
    
    {children}
  </div>
);

const ServicesPage = () => {
  const { t } = useLanguage();
  const { services } = useContent();
  
  const features = [
    ['Zimmerpersonal', 'Supervisoren', 'Hygienestandards', 'Makellose Zimmer'],
    ['Rezeptionisten', 'Mehrsprachig', 'Concierge', 'Problemlösungen'],
    ['Innen & Außen', 'Streifenfrei', 'Sichere Techniken', 'Fassaden'],
    ['Erfahrene Köche', 'Bankett-Service', 'Lebensmittelsicherheit', 'Frühstück']
  ];

  const featuresEn = [
    ['Room Staff', 'Supervisors', 'Hygiene Standards', 'Spotless Rooms'],
    ['Receptionists', 'Multilingual', 'Concierge', 'Problem Solving'],
    ['Indoor & Outdoor', 'Streak-Free', 'Safe Techniques', 'Facades'],
    ['Experienced Chefs', 'Banquet Service', 'Food Safety', 'Breakfast']
  ];

  return (
    <div className="min-h-screen bg-[#F9F9F9]">
      <SEO 
        title={seoData.services.title}
        description={seoData.services.description}
      />
      <Navbar />
      <main>
        <section className="relative pt-40 pb-20">
          <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
            <div className="text-center mb-20">
              <div className="inline-flex items-center gap-3 mb-6">
                <div className="h-px w-12 bg-[#D4AF37]" />
                <span className="text-black/50 text-sm font-medium uppercase tracking-[0.3em]">{t('title', { de: 'Was wir bieten', en: 'What We Offer' })}</span>
                <div className="h-px w-12 bg-[#D4AF37]" />
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-black mb-6">{t('title', translations.services)}</h1>
              <p className="text-xl text-black/60 max-w-2xl mx-auto">{t('subtitle', translations.services)}</p>
            </div>
            <div className="space-y-20">
              {services.map((service, index) => {
                const Icon = iconMap[service.icon];
                const isEven = index % 2 === 0;
                const serviceKeys = ['housekeeping', 'frontOffice', 'windowCleaning', 'kitchen'];
                const serviceKey = serviceKeys[index];
                const currentFeatures = t('features', { de: features[index], en: featuresEn[index] });
                return (
                  <div key={service.id} className="grid lg:grid-cols-2 gap-12 items-center">
                    <div className={`relative ${isEven ? 'lg:order-1' : 'lg:order-2'}`}>
                      <AnimatedFullFrame className="rounded-3xl overflow-hidden shadow-2xl shadow-black/10 bg-[#003366]/5">
                        <img src={service.image} alt={t('title', translations.services[serviceKey])} className="w-full h-auto object-contain" />
                      </AnimatedFullFrame>
                    </div>
                    <div className={isEven ? 'lg:order-2' : 'lg:order-1'}>
                      <div className="w-16 h-16 rounded-2xl bg-[#D4AF37]/10 flex items-center justify-center mb-6">
                        {Icon && <Icon className="w-8 h-8 text-[#D4AF37]" />}
                      </div>
                      <h2 className="text-3xl md:text-4xl font-bold text-black mb-6">{t('title', translations.services[serviceKey])}</h2>
                      <p className="text-black/60 text-lg leading-relaxed mb-8">{t('description', translations.services[serviceKey])}</p>
                      <ul className="space-y-3 mb-8">
                        {currentFeatures.map((f, idx) => (
                          <li key={`feature-${idx}-${f.substring(0, 10)}`} className="flex items-center gap-3"><CheckCircle className="w-5 h-5 text-[#D4AF37] flex-shrink-0" /><span className="text-black/70">{f}</span></li>
                        ))}
                      </ul>
                      <Link to="/contact" className="inline-flex items-center gap-2 text-[#D4AF37] hover:text-[#C9A227] font-medium"><span>{t('cta', { de: 'Jetzt anfragen', en: 'Inquire Now' })}</span><ArrowRight size={18} /></Link>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </section>
        <GoldenGlowLine />
        <section className="py-32 bg-[#003366]">
          <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">{t('ctaTitle', { de: 'Bereit, Ihr Team zu verstärken?', en: 'Ready to strengthen your team?' })}</h2>
            <p className="text-white/60 text-lg mb-10">{t('ctaSubtitle', { de: 'Kontaktieren Sie uns für ein Beratungsgespräch', en: 'Contact us for a consultation' })}</p>
            <Link to="/contact" className="inline-flex items-center gap-3 px-10 py-5 bg-[#D4AF37] text-[#003366] font-bold text-lg rounded-xl hover:bg-[#C9A227] hover:scale-105 transition-all">{t('ctaBtn', { de: 'Kontakt aufnehmen', en: 'Get in Touch' })}<ArrowRight size={22} /></Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default ServicesPage;
